import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ModulePicker, QuotaMeter } from '@features/CardsWorkspace';
import { EmailVerifyBanner } from '@features/EmailVerify';
import {
  fetchTopicBatch,
  fetchTopicBatches,
  generateTopicBatch,
  topicBatchCost,
  topicKeys,
  type TopicBatch,
  type TopicCount,
} from '@features/CardsWorkspace/api/topicCardsApi';
import { TopicIcon } from '@features/TopicCards';
import { useIsSidebarMobile } from '@widgets/Sidebar';
import { useModuleByIdQuery, useUserModulesPageQuery } from '@entities/Module';
import { planKeys, useQuotaQuery } from '@entities/Plan';
import { useAuthStore } from '@entities/User';
import { useProductEventOnMount } from '@shared/analytics';
import { paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { cn } from '@shared/lib';
import { StepsHint } from '@shared/ui';
import { TopicSelection } from './TopicSelection';
import './TopicCardsPage.css';

const COUNTS: TopicCount[] = [10, 20];

export default function TopicCardsPage() {
  const user = useAuthStore((state) => state.user);
  // Открыл подбор слов — в журнал событий для воронки админки (2026-10-21).
  useProductEventOnMount('topics_opened', !!user);
  if (!user) return null;
  return user.emailVerified ? (
    <TopicCardsContent key={user.id} userId={user.id} />
  ) : (
    // Отступ сверху — как у страницы карточек: на телефоне иначе блок уезжал под кнопку меню.
    <div className="mx-auto max-w-[680px] animate-fadeIn px-5 pb-20 pt-20 md:pt-16">
      <EmailVerifyBanner email={user.email} />
    </div>
  );
}

function TopicCardsContent({ userId }: { userId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const isMobile = useIsSidebarMobile();
  const [params, setParams] = useSearchParams();
  const batchId = params.get('batch');
  const moduleId = params.get('moduleId');
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  // Описание — по кнопке «+ описание» (макет A): по умолчанию форма в одну строку.
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [count, setCount] = useState<TopicCount>(10);
  const [formError, setFormError] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const topicRef = useRef<HTMLInputElement>(null);
  // После сетевого сбоя повторяем тот же запрос с тем же ключом: двойного списания нет.
  const requestRef = useRef<{ signature: string; id: string } | null>(null);
  const submittingRef = useRef(false);
  const { data: quota } = useQuotaQuery();
  const modules = useUserModulesPageQuery({ page: 1, size: 100 });
  const selectedModuleQuery = useModuleByIdQuery(moduleId);
  const [createdModules, setCreatedModules] = useState<ModuleSummary[]>([]);
  const allModules = useMemo(() => {
    const available = [
      ...(modules.data?.items ?? []),
      ...createdModules,
      ...(selectedModuleQuery.data ? [selectedModuleQuery.data] : []),
    ];
    return available.filter(
      (module, index) =>
        module.active && !module.deleted && available.findIndex((other) => other.id === module.id) === index,
    );
  }, [modules.data?.items, createdModules, selectedModuleQuery.data]);
  const selectedModule = allModules.find((module) => module.id === moduleId) ?? null;
  const history = useInfiniteQuery({
    queryKey: topicKeys.history(userId),
    queryFn: ({ pageParam, signal }) => fetchTopicBatches(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((total, page) => total + page.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.items.some((batch) => batch.status === 'generating')) ? 5000 : false,
  });
  const batches = useMemo(() => {
    const list = history.data?.pages.flatMap((page) => page.items) ?? [];
    return list.filter((batch, index) => list.findIndex((other) => other.id === batch.id) === index);
  }, [history.data]);
  const previousBatchStatuses = useRef(new Map<string, TopicBatch['status']>());
  useEffect(() => {
    // Возврат квоты должен отобразиться и когда открыта другая подборка.
    const finished = batches.some(
      (item) => previousBatchStatuses.current.get(item.id) === 'generating' && item.status !== 'generating',
    );
    previousBatchStatuses.current = new Map(batches.map((item) => [item.id, item.status]));
    if (finished) {
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    }
  }, [batches, qc]);
  const detail = useQuery({
    queryKey: topicKeys.batch(userId, batchId ?? ''),
    queryFn: ({ signal }) => fetchTopicBatch(batchId!, signal),
    enabled: !!batchId,
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 3000 : false),
  });
  const batch = detail.data;
  const running = batches.some((item) => item.status === 'generating') || batch?.status === 'generating';
  // «+ Новая подборка» (решение владельца 2026-10-16): форма новой подборки — отдельный режим справа,
  // а не строка над текущей подборкой. Без истории форма открыта сразу.
  const composing = params.get('new') === '1' || (!batchId && !history.isPending && !batches.length);
  const openBatch = (id: string) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('batch', id);
      next.delete('new');
      return next;
    });
  const startNew = () =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('batch');
      next.set('new', '1');
      return next;
    });
  const setParam = (name: string, value: string, replace = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(name, value);
        return next;
      },
      { replace },
    );
  useEffect(() => {
    if (!batchId && batches[0] && params.get('new') !== '1')
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('batch', batches[0].id);
          return next;
        },
        { replace: true },
      );
  }, [batchId, batches, params, setParams]);
  useEffect(() => {
    if (batch?.status === 'ready' || batch?.status === 'failed') {
      void qc.invalidateQueries({ queryKey: topicKeys.history(userId) });
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    }
  }, [batch?.id, batch?.status, userId, qc]);
  const generation = useMutation({ mutationFn: generateTopicBatch });
  const cost = topicBatchCost(count);
  const quotaBlocked = !!quota && quota.remaining + quota.bonus < cost;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!topic.trim()) {
      setFormError(t('topicCards.topicRequired'));
      topicRef.current?.focus();
      return;
    }
    if (submittingRef.current || generation.isPending || running || quotaBlocked || bulkBusy) return;
    submittingRef.current = true;
    setFormError(null);
    const signature = JSON.stringify([topic.trim(), description.trim(), count]);
    if (requestRef.current?.signature !== signature) requestRef.current = { signature, id: crypto.randomUUID() };
    try {
      const created = await generation.mutateAsync({ topic, description, count, requestId: requestRef.current.id });
      qc.setQueryData(topicKeys.batch(userId, created.id), created);
      openBatch(created.id);
      requestRef.current = null;
    } catch (error) {
      const apiError =
        error instanceof AxiosError
          ? (error.response?.data as { error?: { code?: string; details?: { batch_id?: string; batchId?: string } } })
          : undefined;
      const code = apiError?.error?.code;
      const existingId = apiError?.error?.details?.batch_id ?? apiError?.error?.details?.batchId;
      if (code === 'TOPIC_BATCH_IN_PROGRESS' && existingId) {
        openBatch(existingId);
      } else {
        setFormError(
          error instanceof AxiosError && error.response?.status === 429
            ? t('topicCards.quotaLow', { count: cost })
            : t(
                error instanceof AxiosError && error.response?.status === 402
                  ? 'topicCards.expired'
                  : 'topicCards.generationError',
              ),
        );
      }
    } finally {
      submittingRef.current = false;
      // Сервер мог принять запрос, даже если ответ потерялся в сети. История
      // восстановит подборку, в том числе когда на повтор уже не хватает квоты.
      void qc.invalidateQueries({ queryKey: topicKeys.history(userId) });
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    }
  };
  // «Ещё слова по теме» и «Уточнить» — новая подборка с той же темой: форма уже заполнена.
  const repeat = (source: TopicBatch) => {
    setTopic(source.topic);
    setDescription(source.description ?? '');
    setDescriptionOpen(!!source.description);
    setCount(source.requestedCount > 10 ? 20 : 10);
    setFormError(null);
    startNew();
  };
  useEffect(() => {
    if (composing) topicRef.current?.focus();
  }, [composing]);
  const date = (value: string) =>
    new Date(value).toLocaleString(i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  // Назад — всегда на главную (решение владельца 2026-10-16): ссылка на модуль из выбора
  // была неочевидной — на подбор приходят не из модуля. Подбор — своя страница без вкладок: слова из
  // словаря добавляются строкой на главной (решение владельца 2026-10-21).
  const backHref = paths.dashboard;
  const backLabel = t('nav.dashboard');
  const formLocked = generation.isPending || bulkBusy;

  return (
    <div className="tc-page animate-fadeIn">
      <Link
        to={backHref}
        className="tc-back"
        aria-disabled={bulkBusy || undefined}
        onClick={(event) => {
          if (bulkBusy) event.preventDefault();
        }}
      >
        ← {backLabel}
      </Link>
      {/* Куда добавятся карточки — прямо в заголовке. */}
      <header className="tc-header">
        <div className="tc-title">
          <h1>{t('cardsWorkspace.tabs.topicShort')}</h1>
          <span className="tc-title-in">{t('cardsWorkspace.header.in')}</span>
          <ModulePicker
            size="lg"
            value={moduleId}
            modules={allModules}
            locked={bulkBusy}
            loading={modules.isPending && !allModules.length}
            difficulty="B1"
            onChange={(id, created) => {
              if (bulkBusy) return;
              if (created) setCreatedModules((list) => [...list, created]);
              setParam('moduleId', id, true);
            }}
          />
        </div>
        <QuotaMeter className="tc-quota" />
      </header>
      {/* Как это работает и сколько стоит — закрывается навсегда (решение владельца 2026-10-16). */}
      <StepsHint
        storageKey="lx.hint.topicSteps"
        steps={(['hint1', 'hint2', 'hint3'] as const).map((key) => ({
          title: t(`topicCards.${key}Title`),
          text: t(`topicCards.${key}Text`),
        }))}
        closeLabel={t('topicCards.hintClose')}
        example={
          <>
            {t('topicCards.hintExample')}{' '}
            <b className="font-semibold text-accent">{t('topicCards.hintExampleTotal')}</b>
          </>
        }
        className="mb-4"
      />
      {modules.isError && (
        <p className="tc-note tc-error">
          {t('topicCards.moduleError')}{' '}
          <button className="tc-link" type="button" onClick={() => void modules.refetch()}>
            {t('topicCards.retry')}
          </button>
        </p>
      )}

      <div className="tc-workspace">
        <aside className="tc-history" aria-label={t('topicCards.history')}>
          <button
            type="button"
            className={cn('tc-new-btn', composing && 'is-active')}
            aria-current={composing ? 'true' : undefined}
            onClick={startNew}
            disabled={bulkBusy}
          >
            <span aria-hidden>+</span> {t('topicCards.newBatch')}
          </button>
          <h2 className="tc-history-title">
            {t('topicCards.history')}
            <span className="tc-mono">{history.data?.pages[0]?.total ?? 0}</span>
          </h2>
          {history.isPending && (
            <p className="tc-note" role="status">
              {t('topicCards.loading')}
            </p>
          )}
          {history.isError && (
            <p className="tc-note tc-error" role="alert">
              {t('topicCards.historyError')}{' '}
              <button className="tc-link" type="button" onClick={() => void history.refetch()}>
                {t('topicCards.retry')}
              </button>
            </p>
          )}
          {!history.isPending && !history.isError && !batches.length && (
            <p className="tc-note">{t('topicCards.historyEmpty')}</p>
          )}
          <div className="tc-history-list">
            {/* На телефоне «Новая подборка» — первой карточкой ленты, а не отдельной кнопкой над ней. */}
            <button
              type="button"
              className={cn('tc-history-item tc-history-new', composing && 'is-active')}
              aria-current={composing ? 'true' : undefined}
              onClick={startNew}
              disabled={bulkBusy}
            >
              <strong>+ {t('topicCards.newBatch')}</strong>
              <span>{t('topicCards.newBatchCost')}</span>
            </button>
            {batches.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={!composing && item.id === batchId ? 'true' : undefined}
                className={cn('tc-history-item', !composing && item.id === batchId && 'is-active')}
                onClick={() => openBatch(item.id)}
                disabled={bulkBusy}
              >
                <strong>{item.topic}</strong>
                <span>
                  <time dateTime={item.createdAt}>{date(item.createdAt)}</time>
                  {' · '}
                  <span className={cn(item.status === 'failed' && 'tc-error', item.status === 'generating' && 'tc-live')}>
                    {item.status === 'ready'
                      ? t('topicCards.wordCount', { count: item.itemCount })
                      : t(`topicCards.status.${item.status}`)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {history.hasNextPage && (
            <button
              className="tc-link tc-load-more"
              type="button"
              disabled={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {history.isFetchingNextPage ? t('topicCards.loading') : t('topicCards.moreHistory')}
            </button>
          )}
        </aside>

        <section className="tc-results" aria-label={t('topicCards.results')}>
          {composing && (
            <div className="tc-new">
              <div className="tc-new-head">
                <TopicIcon className="h-8 w-8 text-accent" />
                <div>
                  <h2>{t('topicCards.emptyTitle')}</h2>
                  <p>{t('topicCards.emptyBody')}</p>
                </div>
              </div>
            {/* Макет A: всё для новой подборки — в одной строке; описание раскрывается по кнопке. */}
            <form className={cn('tc-compose', descriptionOpen && 'has-description')} onSubmit={(event) => void submit(event)}>
              <svg className="tc-compose-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 3.5l1.9 5.1 5.1 1.9-5.1 1.9-1.9 5.1-1.9-5.1-5.1-1.9 5.1-1.9z" />
              </svg>
              <input
                ref={topicRef}
                className="tc-compose-input"
                value={topic}
                onChange={(event) => {
                  setTopic(event.target.value);
                  setFormError(null);
                }}
                maxLength={120}
                required
                aria-label={t('topicCards.topicLabel')}
                placeholder={t(isMobile ? 'topicCards.composePlaceholderShort' : 'topicCards.composePlaceholder')}
                disabled={formLocked}
              />
              <button
                type="button"
                className="tc-compose-more"
                aria-expanded={descriptionOpen}
                disabled={formLocked}
                onClick={() => {
                  // Закрытое описание не уходит в запрос невидимым — при скрытии очищаем.
                  if (descriptionOpen) setDescription('');
                  setDescriptionOpen(!descriptionOpen);
                }}
              >
                {t(descriptionOpen ? 'topicCards.descriptionHide' : 'topicCards.descriptionAdd')}
              </button>
              <div className="tc-seg" role="radiogroup" aria-label={t('topicCards.countLabel')}>
                {COUNTS.map((value) => (
                  <label key={value} className={cn('tc-seg-option', count === value && 'is-on')}>
                    <input
                      type="radio"
                      name="topic-count"
                      value={value}
                      checked={count === value}
                      onChange={() => setCount(value)}
                      disabled={formLocked}
                    />
                    {t('topicCards.wordCount', { count: value })}
                  </label>
                ))}
              </div>
              <button
                className="tc-compose-submit"
                type="submit"
                disabled={!topic.trim() || generation.isPending || running || quotaBlocked || bulkBusy}
                aria-busy={generation.isPending || undefined}
              >
                {/* Цена подбора — на кнопке, а не серым у вариантов «10/20 слов» (решение владельца 2026-10-16).
                    Во время запуска подпись остаётся невидимой под спиннером: кнопка не меняет ширину (ревью 2026-10-19). */}
                <span className="tc-compose-label">
                  {t('topicCards.generate')}
                  <span className="tc-compose-cost">· {t('topicCards.genShort', { count: topicBatchCost(count) })}</span>
                </span>
                {generation.isPending && (
                  <>
                    <span className="tc-compose-spinner" aria-hidden />
                    <span className="sr-only">{t('topicCards.starting')}</span>
                  </>
                )}
              </button>
              {descriptionOpen && (
                <textarea
                  className="tc-compose-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  maxLength={1200}
                  rows={2}
                  autoFocus={!description}
                  aria-label={t('topicCards.descriptionLabel')}
                  placeholder={t('topicCards.descriptionPlaceholder')}
                  disabled={formLocked}
                />
              )}
            </form>
            {(formError || quotaBlocked || running) && (
              <p className="tc-compose-feedback" role={formError ? 'alert' : 'status'}>
                {formError ??
                  (quotaBlocked ? t('topicCards.quotaLow', { count: cost }) : t('topicCards.alreadyRunning'))}
                {quotaBlocked && (
                  <>
                    {' '}
                    <Link to={paths.plans}>{t('topicCards.plans')}</Link>
                  </>
                )}
              </p>
            )}
            </div>
          )}
          {!composing && batchId && detail.isPending && (
            <div className="tc-empty" role="status">
              <span className="tc-spinner" />
              <p>{t('topicCards.loading')}</p>
            </div>
          )}
          {!composing && batchId && detail.isError && (
            <div className="tc-empty" role="alert">
              <h2>{t('topicCards.detailError')}</h2>
              <p>{t('topicCards.detailErrorHint')}</p>
              <button className="tc-secondary" type="button" onClick={() => void detail.refetch()}>
                {t('topicCards.retry')}
              </button>
            </div>
          )}
          {!composing && batch && (
            <>
              <div className="tc-result-header">
                <div className="tc-result-title">
                  <h2>{batch.topic}</h2>
                  <p>
                    {batch.description && <span className="tc-result-description">{batch.description} · </span>}
                    <time dateTime={batch.createdAt}>{date(batch.createdAt)}</time>
                  </p>
                </div>
                {/* Неудачной или ещё не готовой подборке «ещё слова» не нужны — у ошибки своя кнопка. */}
                {batch.status === 'ready' && (
                  <button className="tc-ghost" type="button" disabled={bulkBusy} onClick={() => repeat(batch)}>
                    {t('topicCards.moreOnTopic')}
                  </button>
                )}
              </div>
              {batch.status === 'generating' && (
                <div className="tc-empty" role="status">
                  <span className="tc-spinner" />
                  <h2>{t('topicCards.generatingTitle')}</h2>
                  <p>{t('topicCards.generatingBody')}</p>
                </div>
              )}
              {batch.status === 'failed' && (
                <div className="tc-empty" role="alert">
                  <h2>{t('topicCards.failedTitle')}</h2>
                  {/* Причину пишет бэкенд (или нейросеть) по-русски — в английском интерфейсе общий текст. */}
                  <p className="tc-failed-reason">
                    {(i18n.language.startsWith('ru') && batch.errorMessage) || t('topicCards.failedBody')}
                  </p>
                  <p className="tc-failed-refund">{t('topicCards.failedRefund')}</p>
                  <button className="tc-secondary" type="button" onClick={() => repeat(batch)}>
                    {t('topicCards.tryAgain')}
                  </button>
                </div>
              )}
              {batch.status === 'ready' && (
                <>
                  {batch.items.length < batch.requestedCount && (
                    <p className="tc-note tc-partial">
                      {t('topicCards.partial', { actual: batch.items.length, requested: batch.requestedCount })}
                    </p>
                  )}
                  <TopicSelection
                    key={`${batch.id}:${moduleId ?? ''}`}
                    batch={batch}
                    userId={userId}
                    module={selectedModule}
                    onBusyChange={setBulkBusy}
                  />
                </>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
